import { Collapse, SegmentedControl, UnstyledButton } from "@mantine/core";
import { useLocation, useParams } from "@tanstack/react-router";
import { Archive, ChevronDown, Image as ImageIcon, Layers, Settings2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Catalog } from "../../../api/generated/model";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { PromptBox } from "../../../platform/studio/composer/PromptBox";
import {
  ModelChoice,
  OutputsChoice,
  RatioChoice,
} from "../../../platform/studio/composer/Settings";
import { ComposerErrors, PriceAndStart } from "../../../platform/studio/composer/Start";
import { useComposer } from "../../../platform/studio/composer/use-composer";
import { defaultModel } from "../../../platform/studio/draft";
import { advancedModels } from "../../../platform/studio/image/advanced";
import { AdvancedComposer } from "../../../platform/studio/image/AdvancedComposer";
import { LayerizeComposer, layerizeModel } from "../../../platform/studio/image/LayerizeComposer";
import {
  arrivingPromptTemplate,
  tookPromptTemplate,
} from "../../../platform/studio/image/prompt-template-handoff";
import { PromptTemplates } from "../../../platform/studio/image/PromptTemplates";
import type { AdvancedCarry } from "../../../platform/studio/image/use-advanced";
import { SourceThumb } from "../../../platform/studio/image/SourceThumb";
import { SourceTool } from "../../../ui/SourceTool";
import classes from "./create.module.css";

/** The prototype's two image modes: an image, or an ad static made with ad copy. */
export type ImageMode = "image" | "ad-static";

const NO_SHAPE = () => undefined;
const STARTED = () => undefined;

interface ImageFormProps {
  catalog: Catalog;
  mode: ImageMode;
  onMode: (mode: ImageMode) => void;
}

function ModeSwitch(props: {
  mode: ImageMode;
  onMode: (mode: ImageMode) => void;
  adStatic: boolean;
}) {
  const { mode, onMode, adStatic } = props;
  return (
    <SegmentedControl
      aria-label="Image or ad static"
      radius="xl"
      size="xs"
      classNames={{ root: classes.seg, label: classes.segLabel }}
      value={mode}
      onChange={(value) => onMode(value === "ad-static" ? "ad-static" : "image")}
      data={[
        { value: "image", label: "Image" },
        // An ad static needs a model that makes ad creatives.
        { value: "ad-static", label: "Ad static (with ad copy)", disabled: !adStatic },
      ]}
    />
  );
}

/**
 * The prototype's image composer card on the Studio's own composer (`useComposer`): the same
 * draft, price, checks and start, laid out as the prototype lays it. Ad static is the Studio's
 * Advanced mode; Layerize, from the Advanced door, its Layerize mode.
 */
export function ImageForm(props: ImageFormProps) {
  const { catalog, mode, onMode } = props;
  // What the image draft held when Ad static came on; a link's Ad static starts from nothing.
  const [carry, setCarry] = useState<AdvancedCarry | null>(null);
  const [layerizing, setLayerizing] = useState<PickedImage | null>(null);
  const adStatic = advancedModels(catalog).length > 0;
  const layerize = layerizeModel(catalog.image_models);
  if (layerize !== undefined && layerizing !== null) {
    return (
      <LayerizeComposer
        // In the card, not floating over the page (GlassPanel's inline).
        inline
        model={layerize}
        picture={layerizing}
        onSwitchOff={() => setLayerizing(null)}
        onShapeChange={NO_SHAPE}
        onStarted={STARTED}
      />
    );
  }
  if (mode === "ad-static" && adStatic) {
    // The image draft is not mounted here, so it asks no price while Ad static is on (decision 17).
    const back = () => {
      setCarry(null);
      onMode("image");
    };
    return (
      <>
        <ModeSwitch
          mode={mode}
          onMode={(next) => (next === "image" ? back() : undefined)}
          adStatic
        />
        <AdvancedComposer
          inline
          catalog={catalog}
          carry={
            carry ?? {
              product: null,
              references: [],
              modelId: defaultModel(catalog.image_models).id,
              aspectRatio: null,
            }
          }
          onShapeChange={NO_SHAPE}
          onStarted={STARTED}
          onSwitchOff={back}
        />
      </>
    );
  }
  return (
    <SimpleImage
      catalog={catalog}
      adStatic={adStatic}
      onAdStatic={(held) => {
        setCarry(held);
        onMode("ad-static");
      }}
      onLayerize={setLayerizing}
    />
  );
}

interface SimpleImageProps {
  catalog: Catalog;
  adStatic: boolean;
  /** Ad static coming on, with what the image draft holds. */
  onAdStatic: (carry: AdvancedCarry) => void;
  onLayerize: (picture: PickedImage) => void;
}

/** The prototype's Image mode: the Studio's simple image draft, mounted only while it shows. */
function SimpleImage({ catalog, adStatic, onAdStatic, onLayerize }: SimpleImageProps) {
  const { teamId, brandId } = useParams({ from: "/signed-in/teams/$teamId/brands/$brandId" });
  const [door, setDoor] = useState(false);
  const [choosingLayerize, setChoosingLayerize] = useState(false);
  const c = useComposer({
    catalog,
    medium: "image",
    initialSource: null,
    initialType: undefined,
    clone: undefined,
    // The results column reads the list the stream keeps current; nothing else waits here.
    onStarted: STARTED,
    onShapeChange: NO_SHAPE,
  });
  const layerize = layerizeModel(catalog.image_models);
  // A prompt template's "Use in Image" (Pro's Library) arrives once and is applied as the panel's
  // Apply is; the ref keeps a later render from applying it over the member's edits.
  const location = useLocation();
  const [arrival] = useState(() => arrivingPromptTemplate(location));
  const applied = useRef(false);
  const { applyTemplate } = c;
  useEffect(() => {
    if (arrival === null || applied.current) return;
    applied.current = true;
    tookPromptTemplate(location);
    applyTemplate(arrival.prompt, arrival.images, arrival.templateId);
  }, [arrival, location, applyTemplate]);
  const switcher = (
    <ModeSwitch
      mode="image"
      adStatic={adStatic}
      onMode={(next) =>
        next === "ad-static"
          ? onAdStatic({
              product: c.product,
              references: c.references,
              modelId: c.model.id,
              aspectRatio: c.draft.aspectRatio,
            })
          : undefined
      }
    />
  );
  const full = c.references.length >= c.referencesRoom;
  return (
    <>
      {switcher}
      <div className={classes.field}>
        <span className={classes.fieldLabel}>Prompt</span>
        <div className={classes.prompt}>
          <PromptBox c={c} />
        </div>
      </div>
      {c.canSource ? (
        <>
          <div className={classes.row}>
            <span className={classes.lab}>Product</span>
            {c.products((wrap) => (
              <SourceTool
                label="Products"
                icon={<Archive size={18} strokeWidth={1.5} />}
                view={c.product}
                removeLabel="Remove the product"
                onRemove={c.removeSource}
                onClick={() => c.toggleProducts()}
                disabled={c.product === null && full}
                open={c.productsOpen}
                target={wrap}
              />
            ))}
          </div>
          <div className={classes.row}>
            <span className={classes.lab}>References</span>
            <SourceTool
              label="Reference images"
              icon={<ImageIcon size={18} strokeWidth={1.5} />}
              view={null}
              removeLabel="Remove the source image"
              onRemove={c.removeSource}
              onClick={c.openReferences}
              disabled={full}
            />
            <span className={classes.dim}>
              {c.references.length} / {c.referencesRoom}
            </span>
          </div>
          {c.references.length === 0 ? null : (
            <div className={classes.thumbs}>
              {c.references.map((each) => (
                <SourceThumb
                  key={each.assetId}
                  view={each}
                  removeLabel={`Remove ${each.name}`}
                  onRemove={() => c.removeReference(each.assetId)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}
      <div className={classes.settings}>
        <span className={classes.pair}>
          <span className={classes.lab}>Shape</span>
          <RatioChoice c={c} />
        </span>
        <span className={classes.pair}>
          <span className={classes.lab}>Outputs</span>
          <OutputsChoice c={c} />
        </span>
      </div>
      <UnstyledButton
        className={classes.door}
        aria-expanded={door}
        onClick={() => setDoor(!door)}
      >
        <Settings2 size={14} aria-hidden />
        <span>
          <b>Advanced:</b> prompt templates, Layerize an image
        </span>
        <ChevronDown size={14} className={classes.doorCaret} aria-hidden />
      </UnstyledButton>
      <Collapse expanded={door}>
        <div className={classes.row}>
          <PromptTemplates
            draft={{
              prompt: c.draft.prompt,
              referenceIds: c.references.map((each) => each.assetId),
            }}
            onApply={c.applyTemplate}
          />
          {layerize === undefined ? null : (
            <UnstyledButton className={classes.chip} onClick={() => setChoosingLayerize(true)}>
              <Layers size={14} aria-hidden /> Layerize an image
            </UnstyledButton>
          )}
        </div>
      </Collapse>
      <div className={classes.gobar}>
        <ModelChoice c={c} />
        <span className={classes.grow} />
        <PriceAndStart c={c} />
      </div>
      <ComposerErrors c={c} />
      {c.dialog}
      {choosingLayerize ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          title="Choose image to Layerize"
          subtitle="Choose from your brand Library or upload a new image"
          max={1}
          chosen={[]}
          onClose={() => setChoosingLayerize(false)}
          onConfirm={(picked) => {
            setChoosingLayerize(false);
            const picture = picked[0];
            if (picture !== undefined) onLayerize(picture);
          }}
        />
      ) : null}
    </>
  );
}
